import { motion } from "motion/react";
import { Check } from "lucide-react";
import type { ReactNode } from "react";

interface BrandBulletProps {
  title: string;
  description: string;
}

/**
 * BrandBullet — value bullet shown in the left brand panel.
 * Uses a Maritime-Blue Check icon as the lead mark (replaces the bare
 * brand-color square; the icon reads as "verified / included" which is
 * the right cue for an institutional trust block).
 */
export function BrandBullet({ title, description }: BrandBulletProps) {
  return (
    <div className="flex items-start gap-3">
      <Check
        className="w-4 h-4 mt-0.5 flex-shrink-0 text-[var(--brand)]"
        strokeWidth={2.2}
        aria-hidden="true"
      />
      <div>
        <h3
          className="font-semibold text-sm text-[var(--foreground)] mb-0.5"
          style={{ letterSpacing: "-0.01em" }}
        >
          {title}
        </h3>
        <p className="text-sm text-[var(--muted-foreground)]" style={{ lineHeight: 1.55 }}>
          {description}
        </p>
      </div>
    </div>
  );
}

interface AuthBrandPanelProps {
  /** Section heading above the body content (h2). */
  headline: string;
  /** Short paragraph between headline and body. Optional in minimal variants.
   *  Accepts rich content (e.g. an `<Trans>` element) as well as plain text. */
  intro?: ReactNode;
  /** Arbitrary body content (e.g. AuditLogArtifact, custom block list).
   *  When provided, takes precedence over `bullets`. */
  body?: ReactNode;
  /** Value bullets — typically three. Used when no `body` is provided.
   *  Kept for backwards compatibility with the SignUp value-bullet pattern. */
  bullets?: { title: string; description: string }[];
  /** EU-residency / compliance mono labels rendered at the bottom of
   *  the panel as a trust strip. */
  trustLabels?: string[];
  /** Optional trailing paragraph rendered above the trust strip. */
  footer?: ReactNode;
}

/**
 * AuthBrandPanel — left-column content for the two-column auth layout.
 *
 * Renders the supportive h2 (Plex Serif), an introductory paragraph,
 * three value bullets, and an optional trust strip of EU-residency /
 * regulatory mono labels at the bottom.
 *
 * Page-entrance: staggered fade-up (0ms / 200ms / 300ms delays) per the
 * motion inventory. MotionConfig in AuthShell honors reduced-motion.
 */
export function AuthBrandPanel({
  headline,
  intro,
  body,
  bullets,
  trustLabels,
  footer,
}: AuthBrandPanelProps) {
  return (
    <div className="flex flex-col h-full">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, ease: [0.2, 0, 0, 1], delay: 0.2 }}
        className="mb-6"
      >
        {/* h2 is supportive context — visually subordinate to the right-
            column h1 which is the page action. `break-words` protects the
            layout from extreme inputs like very long workspace names with
            no natural break points (e.g. AcceptInvite's `inviteBrand`). */}
        <h2 className="editorial-display break-words">{headline}</h2>
        {intro && (
          <p className="text-sm text-[var(--muted-foreground)]" style={{ lineHeight: 1.6 }}>
            {intro}
          </p>
        )}
      </motion.div>

      {body ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.2, 0, 0, 1], delay: 0.3 }}
        >
          {body}
        </motion.div>
      ) : (
        bullets &&
        bullets.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32, ease: [0.2, 0, 0, 1], delay: 0.3 }}
            className="space-y-4"
          >
            {bullets.map((b) => (
              <BrandBullet key={b.title} title={b.title} description={b.description} />
            ))}
          </motion.div>
        )
      )}

      {(footer || (trustLabels && trustLabels.length > 0)) && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.2, 0, 0, 1], delay: 0.4 }}
          className="mt-auto pt-8 border-t border-[var(--border)] space-y-3"
        >
          {footer && (
            <p className="text-sm text-[var(--muted-foreground)]" style={{ lineHeight: 1.6 }}>
              {footer}
            </p>
          )}
          {trustLabels && trustLabels.length > 0 && (
            <ul className="space-y-1.5" aria-label="Trust and compliance signals">
              {trustLabels.map((label) => (
                <li key={label} className="auth-mono-label">
                  {label}
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      )}
    </div>
  );
}
